import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Layers, CheckSquare, Square, History, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { PageHeader, Loading } from "@/components/bits";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PrintOverlay } from "@/components/printDocs";

const mm = (n) => (n != null && n !== "" ? `${Number(n).toLocaleString("id-ID", { maximumFractionDigits: 1 })} mm` : "—");

/**
 * Label barcode marking. Isi: proyek, kode marking, singkatan vendor, profil material, panjang material, qty kebutuhan, vendor, QR.
 * (Nomor SPK sengaja tidak dicetak.) sticker=true → tata letak potret (default 70×100 mm).
 */
export const BarcodeLabel = ({ spk, mark, jenis, compact = false, sticker = false, dims }) => {
  const profile = mark.profile || spk.profile || "—";
  const testid = compact ? `barcode-bulk-label-${mark.code}` : "barcode-label";
  if (sticker) {
    return (
      <div className="label-sticker border-2 border-slate-300 bg-white p-2.5 text-black" style={dims ? { width: `${dims.w}mm`, height: `${dims.h}mm` } : undefined} data-testid={testid}>
        <div className="flex items-center gap-1.5 border-b border-slate-400 pb-1">
          <LogoMark size={20} />
          <div className="leading-tight"><div className="font-display text-[9px] font-bold">PT HONGLU BAJA INDONESIA</div><div className="cn text-[8px] text-slate-600">宏睿钢印印尼有限公司</div></div>
        </div>
        <div className="mt-1 truncate text-[10px] font-semibold leading-tight" title={spk.projectName}>{spk.projectName}</div>
        <div className="font-mono text-[9px] text-slate-600">{spk.projectKode} · {jenis === "Unit" ? "Unit" : "Bundle"} · Area {spk.area}</div>
        <div className="mt-1 flex items-center justify-between gap-1">
          <div className="min-w-0 break-all font-display text-[22px] font-black leading-none tracking-wide" data-testid={compact ? undefined : "barcode-label-code"}>{mark.code}</div>
          {spk.vendorSingkatan && <div className="shrink-0 rounded border-2 border-black px-1.5 py-0.5 font-mono text-[13px] font-black tracking-wider" data-testid={compact ? undefined : "barcode-vendor-singkatan"}>{spk.vendorSingkatan}</div>}
        </div>
        <table className="mt-1 w-full text-[9px] leading-tight"><tbody>
          <tr><td className="w-[44%] pr-1 text-slate-600">Profil / <span className="cn">型材</span></td><td className="font-mono font-bold" data-testid={compact ? undefined : "barcode-label-profile"}>{profile}</td></tr>
          <tr><td className="pr-1 text-slate-600">Panjang / <span className="cn">长度</span></td><td className="font-mono font-bold" data-testid={compact ? undefined : "barcode-label-panjang"}>{mm(mark.panjang)}</td></tr>
          <tr><td className="pr-1 text-slate-600">Qty Kebutuhan / <span className="cn">需求</span></td><td className="font-mono font-bold" data-testid={compact ? undefined : "barcode-label-qty"}>{mark.qty} pcs</td></tr>
          <tr><td className="pr-1 text-slate-600">Vendor / <span className="cn">供应商</span></td><td className="truncate">{spk.vendorName}</td></tr>
        </tbody></table>
        <div className="flex flex-1 items-center justify-center py-1">
          <QRCodeSVG value={mark.code} size={dims ? Math.round(Math.min(dims.w, dims.h) * 1.55) : 108} bgColor="#FFFFFF" fgColor="#000000" />
        </div>
        <div className="border-t border-slate-400 pt-1 text-center font-mono text-[11px] font-bold tracking-[0.2em]">{mark.code}</div>
        <div className="text-center text-[7px] text-slate-500">Cetak: {new Date().toLocaleDateString("id-ID")}</div>
      </div>
    );
  }
  return (
    <div className={`${compact ? "label-sheet-item" : "w-[340px]"} rounded-lg border-2 border-slate-300 bg-white p-4 text-black`} data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-slate-300 pb-2">
        <LogoMark size={compact ? 28 : 34} />
        <div className="leading-tight">
          <div className={`font-display font-bold ${compact ? "text-[11px]" : "text-[13px]"}`}>PT HONGLU BAJA INDONESIA</div>
          <div className="cn text-[10px] text-slate-600">宏睿钢印印尼有限公司</div>
        </div>
      </div>
      <div className="mt-2 text-[12px] font-semibold">{spk.projectName}</div>
      <div className="font-mono text-[11px] text-slate-600">{spk.projectKode} · {jenis === "Unit" ? "Unit" : "Bundle"} · Area {spk.area}</div>
      <div className="mt-2 flex items-start justify-between gap-2">
        <div className="break-all font-display text-2xl font-black tracking-wide" data-testid={compact ? undefined : "barcode-label-code"}>{mark.code}</div>
        {spk.vendorSingkatan && <div className="shrink-0 rounded border-2 border-black px-2 py-0.5 font-mono text-[15px] font-black tracking-wider" data-testid={compact ? undefined : "barcode-vendor-singkatan"}>{spk.vendorSingkatan}</div>}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <table className="text-[10px] leading-tight text-slate-700"><tbody>
          <tr><td className="pr-2 text-slate-500">Profil</td><td className="font-mono font-bold text-black" data-testid={compact ? undefined : "barcode-label-profile"}>{profile}</td></tr>
          <tr><td className="pr-2 text-slate-500">Panjang</td><td className="font-mono font-bold text-black" data-testid={compact ? undefined : "barcode-label-panjang"}>{mm(mark.panjang)}</td></tr>
          <tr><td className="pr-2 text-slate-500">Qty Kebutuhan</td><td className="font-mono font-bold text-black" data-testid={compact ? undefined : "barcode-label-qty"}>{mark.qty} pcs</td></tr>
          <tr><td className="pr-2 text-slate-500">Vendor</td><td>{spk.vendorName}{spk.vendorSingkatan ? ` (${spk.vendorSingkatan})` : ""}</td></tr>
          <tr><td className="pr-2 text-slate-500">Cetak</td><td>{new Date().toLocaleDateString("id-ID")}</td></tr>
        </tbody></table>
        <QRCodeSVG value={mark.code} size={compact ? 76 : 86} bgColor="#FFFFFF" fgColor="#000000" />
      </div>
      <div className="mt-2 border-t border-slate-300 pt-1.5 text-center font-mono text-[12px] font-bold tracking-[0.2em]">{mark.code}</div>
    </div>
  );
};

const SIZES = [["a4", "A4 · 2 kolom"], ["sticker", "Stiker 70×100"], ["custom", "Kustom (mm)"]];

export default function Barcode() {
  const [projects, setProjects] = useState([]);
  const [spks, setSpks] = useState([]);
  const [marks, setMarks] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [spkId, setSpkId] = useState("");
  const [markId, setMarkId] = useState("");
  const [jenis, setJenis] = useState("Unit");
  const [bulk, setBulk] = useState(false);
  const [single, setSingle] = useState(false);
  const [picked, setPicked] = useState([]);
  const [labelSize, setLabelSize] = useState("sticker");
  const [custom, setCustom] = useState({ w: 70, h: 100 });
  const [history, setHistory] = useState([]);
  const [searchParams] = useSearchParams();
  const stbId = searchParams.get("stbId");
  const [stbPreset, setStbPreset] = useState(null);
  const presetRef = useRef(null);

  // Preset dari menu STB: /barcode?stbId=... → proyek, SPK & marking STB terpilih otomatis (mode massal)
  useEffect(() => {
    if (!stbId) return;
    api.get(`/stb/${stbId}`).then((r) => { setStbPreset(r.data); presetRef.current = r.data; setProjectId(r.data.projectId); setBulk(true); })
      .catch((e) => toast.error(fmtErr(e)));
  }, [stbId]);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/spk?projectId=${projectId}`).then((r) => { setSpks(r.data); setSpkId(""); setMarkId(""); }).catch(() => {});
    api.get(`/markings?projectId=${projectId}&limit=2000`).then((r) => setMarks(r.data)).catch(() => {});
  }, [projectId]);

  const spk = spks.find((s) => s.id === spkId);
  const spkMarks = marks.filter((m) => m.spkId === spkId);
  const mark = marks.find((m) => m.id === markId) || spkMarks[0];
  useEffect(() => {
    const preset = presetRef.current;
    if (preset && spkId && marks.length) {
      const ids = new Set(preset.markings.map((x) => x.id));
      const inSpk = marks.filter((m) => m.spkId === spkId && ids.has(m.id)).map((m) => m.id);
      if (inSpk.length) { setPicked(inSpk); presetRef.current = null; return; }
    }
    setPicked(marks.filter((m) => m.spkId === spkId).map((m) => m.id));
  }, [spkId, marks]);
  useEffect(() => {
    const preset = presetRef.current;
    if (!preset || spkId || !spks.length || !marks.length || projectId !== preset.projectId) return;
    const ids = new Set(preset.markings.map((x) => x.id));
    const count = {};
    marks.filter((m) => ids.has(m.id)).forEach((m) => { count[m.spkId] = (count[m.spkId] || 0) + 1; });
    const top = Object.keys(count).sort((a, b) => count[b] - count[a])[0];
    if (top) setSpkId(top);
    if (Object.keys(count).length > 1) toast.info("STB ini mencakup beberapa SPK — label dicetak per SPK, ganti SPK untuk sisanya");
  }, [spks, marks, spkId, projectId]);
  const loadHistory = useCallback(() => {
    if (!spkId) { setHistory([]); return; }
    api.get(`/label-prints?spkId=${spkId}`).then((r) => setHistory(r.data)).catch(() => {});
  }, [spkId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  const printCount = history.reduce((acc, h) => { h.codes.forEach((c) => { acc[c] = (acc[c] || 0) + 1; }); return acc; }, {});
  const bulkMarks = spkMarks.filter((m) => picked.includes(m.id));
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const isSticker = labelSize !== "a4";
  const dims = labelSize === "custom" ? { w: Math.max(40, Number(custom.w) || 70), h: Math.max(30, Number(custom.h) || 100) } : { w: 70, h: 100 };
  const sizeLabel = labelSize === "a4" ? "A4 2 kolom" : `Stiker ${dims.w}×${dims.h} mm`;
  const pageSize = isSticker ? `${dims.w}mm ${dims.h}mm` : "A4";

  const recordPrint = async (codes, mode) => {
    try {
      const { data } = await api.post("/label-prints", { spkId: spk.id, spkNo: spk.no, projectKode: spk.projectKode, vendorLabel: spk.vendorSingkatan || spk.vendorName, codes, jenis, size: mode === "single" ? `single · ${sizeLabel}` : sizeLabel, mode });
      if (data.duplicates?.length) toast.warning(`${data.duplicates.length} label pernah dicetak sebelumnya: ${data.duplicates.slice(0, 5).join(", ")}${data.duplicates.length > 5 ? "…" : ""}`);
      loadHistory();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const cetak = () => {
    if (!mark) { toast.error("Pilih proyek, SPK, dan marking terlebih dahulu"); return; }
    setSingle(true);
  };

  const [q, setQ] = useState("");
  const shownMarks = spkMarks.filter((m) => !q || `${m.code} ${m.profile || ""}`.toLowerCase().includes(q.toLowerCase()));
  const selStyle = "h-9 border-slate-800 bg-slate-900/70 text-xs";

  return (
    <div className="space-y-4">
      <PageHeader id="Cetak Barcode & Marking" zh="条码打印与构件标识" desc="Pilih marking → preview label → cetak. Label memuat profil, panjang & qty kebutuhan material."
        actions={<Link to="/barcode-riwayat" className="inline-flex items-center gap-1.5 rounded border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800" data-testid="barcode-history-link"><History className="h-3.5 w-3.5 text-sky-400" />Riwayat Cetak <span className="cn text-slate-500">打印记录</span></Link>} />
      {stbPreset && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="barcode-stb-preset">
          Label untuk STB <span className="font-mono font-bold">{stbPreset.no}</span> · {stbPreset.projectName} · {stbPreset.vendorSingkatan || stbPreset.vendorName} · {stbPreset.markings.length} marking dipilih otomatis
        </div>
      )}

      {/* toolbar: proyek · SPK · jenis · ukuran */}
      <div className="panel flex flex-wrap items-end gap-3 p-3" data-testid="barcode-form-panel">
        <div className="min-w-[260px] flex-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Proyek / 项目</Label>
          <Select value={projectId} onValueChange={setProjectId} data-testid="barcode-project-select">
            <SelectTrigger className={`mt-1 ${selStyle}`}><SelectValue placeholder="Pilih Proyek" /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.kode} · {p.nama}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[240px] flex-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">SPK / 派工单</Label>
          <Select value={spkId} onValueChange={setSpkId} disabled={!projectId} data-testid="barcode-spk-select">
            <SelectTrigger className={`mt-1 ${selStyle}`}><SelectValue placeholder={projectId ? "Pilih SPK" : "Pilih proyek dulu"} /></SelectTrigger>
            <SelectContent className="bg-[#0D1524] border-slate-800 max-h-56">
              {spks.map((s) => <SelectItem key={s.id} value={s.id}>{s.no} · {s.vendorSingkatan || s.vendorName}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Jenis / 类型</Label>
          <div className="mt-1 flex gap-1">
            {["Unit", "Bundle"].map((j) => (
              <button key={j} onClick={() => setJenis(j)} className={`rounded border px-3 py-2 text-xs transition-colors ${jenis === j ? "border-sky-500/60 bg-sky-500/10 text-sky-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`} data-testid={`barcode-jenis-${j.toLowerCase()}`}>{j === "Unit" ? "◉ Unit" : "○ Bundle"}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Ukuran Label / 标签尺寸</Label>
          <div className="mt-1 flex gap-1">
            {SIZES.map(([k, t]) => (
              <button type="button" key={k} onClick={() => setLabelSize(k)} className={`rounded border px-2.5 py-2 text-[11px] transition-colors ${labelSize === k ? "border-sky-500/60 bg-sky-500/10 text-sky-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`} data-testid={`barcode-size-${k}`}>{t}</button>
            ))}
          </div>
        </div>
        {labelSize === "custom" && (
          <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="barcode-custom-size">
            <Input type="number" min="40" max="300" value={custom.w} onChange={(e) => setCustom({ ...custom, w: e.target.value })} className="h-9 w-20 border-slate-800 bg-slate-900/70 font-mono text-xs" data-testid="barcode-custom-width" />
            <span>×</span>
            <Input type="number" min="30" max="300" value={custom.h} onChange={(e) => setCustom({ ...custom, h: e.target.value })} className="h-9 w-20 border-slate-800 bg-slate-900/70 font-mono text-xs" data-testid="barcode-custom-height" />
            <span>mm</span>
          </div>
        )}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_1fr]">
        {/* pemilihan marking — fokus utama */}
        <div className="panel flex flex-col p-4" data-testid="barcode-bulk-panel">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-bold text-slate-100">Pilih Marking <span className="cn text-xs font-normal text-slate-500">选择构件</span></h3>
            <span className="rounded border border-slate-800 px-2 py-0.5 font-mono text-[11px] text-slate-400" data-testid="barcode-pick-count">{picked.length} / {spkMarks.length} dipilih</span>
            <div className="ml-auto flex items-center gap-1.5">
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari marking / profil…" disabled={!spkId} className="h-8 w-48 border-slate-800 bg-slate-900/70 text-xs" data-testid="barcode-marking-search" />
              <button type="button" onClick={() => setPicked([...new Set([...picked, ...shownMarks.map((m) => m.id)])])} disabled={!shownMarks.length} className="rounded border border-sky-500/40 bg-sky-500/10 px-2.5 py-1.5 text-xs text-sky-300 hover:bg-sky-500/20 disabled:opacity-40" data-testid="barcode-pick-all">Semua ({shownMarks.length})</button>
              <button type="button" onClick={() => setPicked([])} className="rounded border border-slate-800 px-2.5 py-1.5 text-xs text-slate-400 hover:text-slate-200" data-testid="barcode-pick-none">Kosongkan</button>
            </div>
          </div>
          <div className="max-h-[62vh] min-h-[320px] flex-1 overflow-y-auto rounded border border-slate-800" data-testid="barcode-pick-list">
            {!spkId ? (
              <div className="flex h-full min-h-[320px] items-center justify-center py-16 text-center text-sm text-slate-500" data-testid="barcode-preview-empty">Pilih proyek → SPK untuk menampilkan marking<br /><span className="cn text-xs text-slate-600">选择项目 → 派工单</span></div>
            ) : shownMarks.length === 0 ? <div className="py-16 text-center text-sm text-slate-500">Tidak ada marking sesuai pencarian</div> : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#0D1524] text-[10px] uppercase tracking-wider text-slate-500">
                  <tr><th className="w-10 px-2 py-2" /><th className="px-2 py-2 text-left">Marking</th><th className="px-2 py-2 text-left">Profil</th><th className="px-2 py-2 text-right">Panjang</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">Cetak</th></tr>
                </thead>
                <tbody>
                  {shownMarks.map((m) => {
                    const on = picked.includes(m.id), active = mark?.id === m.id;
                    return (
                      <tr key={m.id} onClick={() => setMarkId(m.id)} className={`cursor-pointer border-t border-slate-800/70 ${active ? "bg-amber-500/10 ring-1 ring-inset ring-amber-500/40" : on ? "bg-sky-500/5" : ""} hover:bg-slate-800/50`} data-testid={`barcode-row-${m.code}`}>
                        <td className="px-2 py-2 text-center" onClick={(e) => { e.stopPropagation(); togglePick(m.id); }}>
                          <button type="button" className="flex items-center" data-testid={`barcode-pick-${m.code}`}>{on ? <CheckSquare className="h-5 w-5 text-sky-400" /> : <Square className="h-5 w-5 text-slate-500" />}</button>
                        </td>
                        <td className="px-2 py-2 font-mono text-base font-bold text-amber-400">{m.code}</td>
                        <td className="px-2 py-2 text-xs text-slate-300">{m.profile || spk?.profile || "—"}</td>
                        <td className="px-2 py-2 text-right font-mono text-xs text-slate-400">{mm(m.panjang)}</td>
                        <td className="px-2 py-2 text-right font-mono text-slate-200">{m.qty} <span className="text-[10px] text-slate-500">pcs</span></td>
                        <td className="px-2 py-2 text-right">{printCount[m.code] > 0 ? <span className="inline-flex items-center gap-0.5 rounded border border-rose-500/30 bg-rose-500/10 px-1.5 py-0.5 font-mono text-[10px] text-rose-300" title="Sudah pernah dicetak" data-testid={`barcode-printed-${m.code}`}><Copy className="h-2.5 w-2.5" />{printCount[m.code]}×</span> : <span className="text-[10px] text-slate-600">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-slate-500">Klik baris = preview · centang = ikut cetak massal</span>
            <Button onClick={() => { if (!bulkMarks.length) { toast.error("Centang minimal 1 marking"); return; } setBulk(true); }} disabled={!bulkMarks.length} className="ml-auto bg-sky-600 hover:bg-sky-500" data-testid="barcode-bulk-print-button">
              <Layers className="mr-2 h-4 w-4" />Cetak Label Terpilih ({bulkMarks.length}) / 批量打印 · {sizeLabel}
            </Button>
          </div>
        </div>

        {/* preview label — besar */}
        <div className="panel flex flex-col p-4" data-testid="barcode-preview-panel">
          <div className="mb-3 flex items-center gap-2">
            <h3 className="font-display text-base font-bold text-slate-100">Preview Label <span className="cn text-xs font-normal text-slate-500">标签预览</span></h3>
            <span className="ml-auto font-mono text-[11px] text-slate-500">{sizeLabel}{mark ? ` · ${mark.code}` : ""}</span>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-auto rounded border border-slate-800 bg-[#0B1322] p-6">
            {mark && spk ? (
              <div style={{ transform: isSticker ? "scale(1.45)" : "scale(1.15)", transformOrigin: "center", margin: isSticker ? `${Math.round(dims.h * 0.9)}px 0` : "24px 0" }}>
                <BarcodeLabel spk={spk} mark={mark} jenis={jenis} sticker={isSticker} dims={isSticker ? dims : undefined} />
              </div>
            ) : (
              <div className="py-20 text-center text-sm text-slate-500">Pilih marking di kiri untuk melihat preview<br /><span className="cn text-xs text-slate-600">在左侧选择构件以预览</span></div>
            )}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="rounded border border-slate-800 p-2.5"><div className="text-slate-500">Qty Kebutuhan</div><div className="font-mono text-lg text-slate-100" data-testid="barcode-info-qty">{mark ? mark.qty : "—"}</div></div>
            <div className="rounded border border-slate-800 p-2.5"><div className="text-slate-500">Panjang</div><div className="font-mono text-lg text-slate-100" data-testid="barcode-info-panjang">{mark ? mm(mark.panjang) : "—"}</div></div>
            <div className="rounded border border-slate-800 p-2.5"><div className="text-slate-500">Profil</div><div className="truncate font-mono text-sm text-slate-100" title={mark?.profile || spk?.profile} data-testid="barcode-info-profile">{mark ? (mark.profile || spk?.profile || "—") : "—"}</div></div>
          </div>
          <Button onClick={cetak} disabled={!mark} className="mt-3 w-full bg-amber-600 hover:bg-amber-500" data-testid="barcode-print-button"><Printer className="mr-2 h-4 w-4" />Buat & Cetak Label Ini / 打印 ({sizeLabel})</Button>
        </div>
      </div>

      {single && spk && mark && (
        <PrintOverlay testid="barcode-single-overlay" pageSize={pageSize} maxWidth="max-w-fit" onClose={() => setSingle(false)} onPrint={() => recordPrint([mark.code], "single")}>
          <div className="label-single" data-testid="barcode-single-sheet">
            <BarcodeLabel spk={spk} mark={mark} jenis={jenis} compact sticker={isSticker} dims={isSticker ? dims : undefined} />
          </div>
        </PrintOverlay>
      )}

      {bulk && spk && (
        <PrintOverlay testid="barcode-bulk-overlay" pageSize={pageSize} maxWidth={isSticker ? "max-w-fit" : undefined} onClose={() => setBulk(false)} onPrint={() => recordPrint(bulkMarks.map((m) => m.code), "bulk")}>
          <div className={isSticker ? "label-sticker-sheet" : "label-sheet"} data-testid="barcode-bulk-sheet" data-size={labelSize}>
            {labelSize === "a4" && (
              <div className="label-sheet-head">
                <span className="font-display font-bold">{spk.projectKode} {spk.projectName}</span>
                <span className="font-mono">{spk.vendorSingkatan || spk.vendorName} · {bulkMarks.length} label · {jenis}</span>
              </div>
            )}
            <div className={isSticker ? "label-sticker-list" : "label-grid"}>
              {bulkMarks.map((m) => <BarcodeLabel key={m.id} spk={spk} mark={m} jenis={jenis} compact sticker={isSticker} dims={dims} />)}
            </div>
          </div>
        </PrintOverlay>
      )}
    </div>
  );
}
